import React, { useState, useContext, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from './api/api';
import { AuthContext } from '../App';
import TrackGainsModal from './TrackGainsModal';
import SimilarRacesModal from './SimilarRacesModal';
import SimilarWeatherModal from './SimilarWeatherModal';
import RecommendUpgradeModal from './RecommendUpgradeModal';
import WearRecommendationsModal from './WearRecommendationsModal';
import TrackPicker from './TrackPicker';
import UserImpersonationPicker from './UserImpersonationPicker';

const SetupPage = () => {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('id');

  const hasFetched = useRef(false);
  const abortControllerRef = useRef(null);

  const [title, setTitle] = useState('Setup Page');
  const [totalLapsFromApi, setTotalLapsFromApi] = useState('');
  const [trackId, setTrackId] = useState(urlId ? parseInt(urlId, 10) : null);
  // The track of the actual upcoming shared race (same for every manager/group in
  // the game) — used to only show the weather-similarity block when the page is
  // showing that race, not some unrelated track someone picked via TrackPicker.
  const [todayTrackId, setTodayTrackId] = useState(null);
  const [selectedTrackName, setSelectedTrackName] = useState(null);
  const [impersonateUserId, setImpersonateUserId] = useState('');
  const [submitError, setSubmitError] = useState(null);

  // Keep trackId in sync with the URL — lets TrackPicker switch tracks by
  // just updating the `id` query param, without a full page reload.
  useEffect(() => {
    if (urlId) setTrackId(parseInt(urlId, 10));
  }, [urlId]);

  // Track name
  useEffect(() => {
    if (!trackId) { setSelectedTrackName(null); return; }
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const fetchTrack = async () => {
      try {
        const res = await api.get(`/track/${trackId}`, { signal: controller.signal });
        setSelectedTrackName(res.data.name);
      } catch (err) {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') console.error('Failed to fetch track name:', err);
        setSelectedTrackName(null);
      }
    };
    fetchTrack();
    return () => controller.abort();
  }, [trackId]);

  // PHA offsets + Lap/Race Gain for the current track
  const [trackGains, setTrackGains] = useState(null);
  const [trackGainsError, setTrackGainsError] = useState(null);
  const [showGainsModal, setShowGainsModal] = useState(false);
  const [showSimilarRacesModal, setShowSimilarRacesModal] = useState(false);
  const [showSimilarWeatherModal, setShowSimilarWeatherModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [wearRecommendations, setWearRecommendations] = useState(null);
  const [wearModalColumn, setWearModalColumn] = useState(null); // 'finish' | 'testing' | null

  useEffect(() => {
    if (!trackId) { setTrackGains(null); setTrackGainsError(null); return; }
    let cancelled = false;
    const fetchGains = async () => {
      try {
        const res = await api.get(`/track/gains/${trackId}`);
        if (!cancelled) { setTrackGains(res.data); setTrackGainsError(null); }
      } catch (err) {
        if (!cancelled) {
          setTrackGains(null);
          setTrackGainsError(err.response?.status === 403 ? null : 'Failed to load gains.');
        }
      }
    };
    fetchGains();
    return () => { cancelled = true; };
  }, [trackId]);

  // Rank PHA offsets: most negative -> magenta, second most negative -> blue, rest -> none.
  const phaColors = (() => {
    const p = trackGains?.phaOffsetPower;
    const h = trackGains?.phaOffsetHandling;
    const a = trackGains?.phaOffsetAcceleration;
    if (p == null || h == null || a == null) return {};
    const ranked = [
      { key: 'power', v: p }, { key: 'handling', v: h }, { key: 'acceleration', v: a },
    ].sort((x, y) => x.v - y.v);
    const colorFor = { [ranked[0].key]: '#FF00FF', [ranked[1].key]: '#4472C4' };
    return colorFor;
  })();

  const [weather, setWeather] = useState({
    temperatureQ1: '', temperatureQ2: '', temperatureRace: '',
    isDryQ1: false, isWetQ1: false,
    isDryQ2: false, isWetQ2: false,
    isDryRace: false, isWetRace: false,
  });

  const [tyreSuppliers, setTyreSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedCompound, setSelectedCompound] = useState('Extra Soft');

  const [tyresLeft, setTyresLeft] = useState(Array(10).fill(''));
  const [tyresLeftCorrected, setTyresLeftCorrected] = useState(Array(10).fill(''));
  const [tyreTotals, setTyreTotals] = useState({
    total: '', totalExtraSoft: '', totalSoft: '', totalMedium: '', totalHard: '', totalRain: '', model: '',
  });
  const [tyreTotalsCorrected, setTyreTotalsCorrected] = useState({
    total: '', totalExtraSoft: '', totalSoft: '', totalMedium: '', totalHard: '', totalRain: '', model: '',
  });

  // Today update
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetchTodayUpdate = async () => {
      try {
        const res = await api.get('/update/today');
        const data = res.data;
        if (data.apiRequestsRemaining !== undefined) setApiRequests(data.apiRequestsRemaining);
        setTitle(`${data.trackName} (S${data.season}, R${data.raceNumber})`);
        if (data.trackId) setTodayTrackId(data.trackId);
        if (!urlId && data.trackId) setTrackId(data.trackId);
        if (data.weather) {
          setWeather({
            temperatureQ1: data.weather.temperatureQ1 ?? '',
            temperatureQ2: data.weather.temperatureQ2 ?? '',
            temperatureRace: data.weather.temperatureRace ?? '',
            isDryQ1: data.weather.isDryQ1 ?? false,
            isWetQ1: data.weather.isWetQ1 ?? false,
            isDryQ2: data.weather.isDryQ2 ?? false,
            isWetQ2: data.weather.isWetQ2 ?? false,
            isDryRace: data.weather.isDryRace ?? false,
            isWetRace: data.weather.isWetRace ?? false,
          });
          if (data.laps !== undefined) setTotalLapsFromApi(data.laps);
        }
      } catch (err) { console.error('Update today failed:', err); }
    };
    fetchTodayUpdate();
  }, []);

  // Tyre suppliers
  useEffect(() => {
    const fetchTyreSuppliers = async () => {
      try {
        const res = await api.get('/tyre');
        const sorted = res.data.sort((a, b) => a.id - b.id);
        setTyreSuppliers(sorted);
      } catch (err) { console.error('Failed to fetch tyre suppliers:', err); }
    };
    fetchTyreSuppliers();
  }, []);

  const [driverInput, setDriverInput] = useState('');
  const [carInput, setCarInput] = useState('');
  const [laps, setLaps] = useState(Array(10).fill(''));
  const [testingStints, setTestingStints] = useState([34, 33, 33]);
  const [fuels, setFuels] = useState(Array(10).fill(''));
  const [boosts, setBoosts] = useState(Array(10).fill(''));
  const [fuelWet, setFuelWet] = useState(Array(10).fill(''));
  const [ct, setCt] = useState(0);
  const [version, setVersion] = useState(1);
  const [wobbling, setWobbling] = useState('1');
  const [overcut, setOvercut] = useState('Yes');

  const [q1Data, setQ1Data] = useState({});
  const [q2Data, setQ2Data] = useState({});
  const [raceData, setRaceData] = useState({});
  const [tcdData, setTcdData] = useState({ q1: 0, q2: 0, race: 0 });
  const [driverStats, setDriverStats] = useState({});
  const [carLevels, setCarLevels] = useState({});
  const [carWearStart, setCarWearStart] = useState({});
  const [carWearFinish, setCarWearFinish] = useState({});
  const [carWearFinishAndTestings, setCarWearFinishAndTestings] = useState({});
  const [fuelData, setFuelData] = useState({});

  const { tokens, setApiRequests, userRole } = useContext(AuthContext);
  const isAdmin = userRole === 'ADMIN';
  // Similar races, Recommend upgrade, Lap/Race Gain — manager tooling, not
  // self-service like Energy Forecast below.
  const isPrivileged = ['ADMIN', 'UPDATER', 'FORCE_ONE', 'BRUTE_FORCE'].includes(userRole);
  // Energy Forecast is self-service for every account now — the backend always
  // computes it against the caller's own GPRO data unless the role is one of
  // ADMIN/UPDATER/FORCE_ONE/BRUTE_FORCE (those can pass driver/car/trackId to
  // compute for someone else, same as before).
  const canSeeSetupExtras = true;
  const canSeeQ1Forecast = ['ADMIN', 'FORCE_ONE'].includes(userRole);
  // Weather-similarity block below — every role except OTHER.
  const canSeeSimilarWeather = !!userRole && userRole !== 'OTHER';

  const [similarWeatherRaces, setSimilarWeatherRaces] = useState(null);
  useEffect(() => {
    if (!canSeeSimilarWeather) return;
    let cancelled = false;
    api.get('/weather/similar-races')
      .then(res => { if (!cancelled) setSimilarWeatherRaces(res.data); })
      .catch(() => { if (!cancelled) setSimilarWeatherRaces(null); });
    return () => { cancelled = true; };
  }, [canSeeSimilarWeather]);

  const [energyForecast, setEnergyForecast] = useState(null);
  const [energyForecastLoading, setEnergyForecastLoading] = useState(false);
  const [energyForecastError, setEnergyForecastError] = useState(null);
  // 'CURRENT' (default, our production model) or 'MARTIN' (alternative formula,
  // see EnergyForecastServiceImpl.calculateEnergyForecastMartinRaw on the backend).
  const [energyFormula, setEnergyFormula] = useState('CURRENT');

  const [q1Forecast, setQ1Forecast] = useState(null);
  const [q1ForecastLoading, setQ1ForecastLoading] = useState(false);
  const [q1ForecastError, setQ1ForecastError] = useState(null);

  // PHA (carPower/carHandling/carAcceleration) only comes back from /setup/race
  // when it actually had to query GPRO Practice1 (i.e. car/carWear were missing).
  // Once we have it, keep it around for the rest of the session and reuse it on
  // every later submit — even after car/carWear get auto-refilled from the
  // response and stop triggering Practice1 themselves. See
  // tasks/setup-endpoints-missing-fields/TASK.md "Обновление 2026-08-20".
  const [cachedPha, setCachedPha] = useState(null);
  const [forceRefreshEnergy, setForceRefreshEnergy] = useState(false);
  const [forceRefreshQ1, setForceRefreshQ1] = useState(false);

  // A cached PHA belongs to whichever GPRO account it was fetched for — stop
  // reusing it once the admin switches to computing for a different user.
  useEffect(() => { setCachedPha(null); }, [impersonateUserId]);

  const handleLapChange = (i, v) => { const c = [...laps]; c[i] = v; setLaps(c); };
  const handleBoostChange = (i, v) => { const c = [...boosts]; c[i] = v; setBoosts(c); };
  const handleTestingStintChange = (i, v) => { const c = [...testingStints]; c[i] = v; setTestingStints(c); };
  const addTestingStint = () => setTestingStints(s => s.length >= 10 ? s : [...s, 0]);
  const removeTestingStint = (i) => setTestingStints(s => s.length <= 1 ? s : s.filter((_, idx) => idx !== i));
  const redistributeTestingStints = () => setTestingStints(s => evenFrontLoadedSplit(100, s.length).map(v => Math.min(v, 50)));
  const handleCtChange = (v) => setCt(v);
  const handleTempChange = (field, value) => setWeather(prev => ({ ...prev, [field]: value }));
  const handleDryChange = (session, checked) => setWeather(prev => ({ ...prev, [`isDry${session}`]: checked }));
  const handleWetChange = (session, checked) => setWeather(prev => ({ ...prev, [`isWet${session}`]: checked }));

  const carKeyMap = {
    chassis: 'chassis', engine: 'engine', 'front wing': 'frontWing',
    'rear wing': 'rearWing', underbody: 'underbody', sidepods: 'sidepods',
    cooling: 'cooling', gearbox: 'gearbox', brakes: 'brakes',
    suspension: 'suspension', electronics: 'electronics',
  };

  const totalEnteredLaps = laps.reduce((sum, v) => {
    const n = parseInt(v, 10);
    return sum + (isNaN(n) ? 0 : n);
  }, 0);

  // ── Pit-stop tactics ────────────────────────────────────────────
  // Tyres start each stint fresh at 100%. `wobbling` starts once a tyre's
  // remaining life drops to WOBBLE_FLOOR% or below (loses speed from there).
  const WOBBLE_FLOOR = 18;

  // Largest lap count L such that (100 - L * wearPerLap) stays above `floor`
  // (or at/above it, when `inclusive`).
  const maxLapsForFloor = (wearPerLap, floor, inclusive) => {
    if (!wearPerLap || wearPerLap <= 0) return Infinity;
    let laps = 0;
    while (laps < 1000) {
      const remainingNext = 100 - (laps + 1) * wearPerLap;
      const stop = inclusive ? remainingNext < floor : remainingNext <= floor;
      if (stop) break;
      laps += 1;
    }
    return laps;
  };

  // Max laps a single stint can run: the safe (non-wobbling) laps, plus the
  // number of tolerated wobbling laps, capped by full tyre depletion (0%).
  const maxLapsPerStint = (wearPerLap, wobblingTolerance) => {
    const fullDepletionCap = maxLapsForFloor(wearPerLap, 0, true);
    if (wobblingTolerance === 'unlimited') return fullDepletionCap;
    const safeLaps = maxLapsForFloor(wearPerLap, WOBBLE_FLOOR, false);
    return Math.min(safeLaps + Number(wobblingTolerance), fullDepletionCap);
  };

  // Even split, front-loading any remainder laps onto the earlier stints.
  const evenFrontLoadedSplit = (totalLaps, stints) => {
    const base = Math.floor(totalLaps / stints);
    const remainder = totalLaps - base * stints;
    return Array.from({ length: stints }, (_, i) => base + (i < remainder ? 1 : 0));
  };

  // Fill every stint up to its cap, front to back — every stint (including
  // the last) must respect the wobbling floor, since a wobbling final stint
  // is just as bad as a wobbling mid-race one.
  const greedyCappedSplit = (totalLaps, stints, capPerStint) => {
    const result = [];
    let remaining = totalLaps;
    for (let i = 0; i < stints; i++) {
      const take = Math.min(capPerStint, remaining);
      result.push(take);
      remaining -= take;
    }
    // Safety net: only hit if the caller passed a `stints` count that turns
    // out too small for `capPerStint` (shouldn't happen — see reqStints below).
    if (remaining > 0 && result.length > 0) result[result.length - 1] += remaining;
    return result;
  };

  // Returns 1 or 2 tactic options for a compound:
  //  - the "natural" pit count (total wear / 100), and
  //  - if that natural count can't keep every stint within the wobbling cap,
  //    a second option with enough extra pit stops to make it fit.
  const getCompoundTactics = (totalWearPercent, raceLaps) => {
    const baseStints = Math.max(1, Math.ceil(totalWearPercent / 100));

    if (overcut === 'No') {
      return [{ stints: baseStints, laps: evenFrontLoadedSplit(raceLaps, baseStints), fits: true }];
    }

    const wearPerLap = totalWearPercent / raceLaps;
    const cap = maxLapsPerStint(wearPerLap, wobbling);
    const fits = baseStints * cap >= raceLaps;

    if (fits) {
      return [{ stints: baseStints, laps: greedyCappedSplit(raceLaps, baseStints, cap), fits: true }];
    }

    const reqStints = Math.max(baseStints + 1, Math.ceil(raceLaps / cap));
    return [
      { stints: baseStints, laps: evenFrontLoadedSplit(raceLaps, baseStints), fits: false },
      { stints: reqStints, laps: greedyCappedSplit(raceLaps, reqStints, cap), fits: true },
    ];
  };

  const compoundTactics = [
    { key: 'totalExtraSoft', label: 'Extra Soft' },
    { key: 'totalSoft', label: 'Soft' },
    { key: 'totalMedium', label: 'Medium' },
    { key: 'totalHard', label: 'Hard' },
  ];

  const applyTactic = (compoundLabel, lapsArr) => {
    const newLaps = Array(10).fill('');
    lapsArr.forEach((v, i) => { if (i < 10) newLaps[i] = String(v); });
    setLaps(newLaps);
    setSelectedCompound(compoundLabel);
  };

  // Wobbling laps actually incurred per stint, derived from the submitted
  // laps and the tyre-left % the server returned for that stint (not from
  // the tactic planner — this reflects whatever was actually entered/sent).
  const stintWobblingLaps = laps.map((lapVal, i) => {
    const lapsNum = parseInt(lapVal, 10);
    const remainNum = parseFloat(tyresLeft[i]);
    if (!lapsNum || isNaN(remainNum)) return null;
    const wearUsed = 100 - remainNum;
    if (wearUsed <= 0) return 0;
    const wearPerLap = wearUsed / lapsNum;
    const safeLaps = maxLapsForFloor(wearPerLap, WOBBLE_FLOOR, false);
    return Math.max(0, lapsNum - safeLaps);
  });

  const parseDriver = (input) => {
    if (!input.trim()) return null;
    const driver = {};
    const lines = input.split('\n').map(l => l.trim()).filter(l => l);
    const energyLine = lines.find(l => /^\d+%/.test(l));
    if (energyLine) driver.energy = parseInt(energyLine, 10);
    lines.forEach(line => {
      const match = line.match(/^([A-Za-z\s\(\)]+):\s*(\d+)/);
      if (!match) return;
      const key = match[1].trim().toLowerCase().replace(/\s|\(|\)/g, '');
      const val = parseInt(match[2], 10);
      switch (key) {
        case 'overall': driver.overall = val; break;
        case 'concentration': driver.concentration = val; break;
        case 'talent': driver.talent = val; break;
        case 'aggressiveness': driver.aggressiveness = val; break;
        case 'experience': driver.experience = val; break;
        case 'technicalinsight': driver.technicalInsight = val; break;
        case 'stamina': driver.stamina = val; break;
        case 'charisma': driver.charisma = val; break;
        case 'motivation': driver.motivation = val; break;
        case 'reputation': driver.reputation = val; break;
        case 'weightkg': case 'weight': driver.weight = val; break;
        case 'age': driver.age = val; break;
        default: break;
      }
    });
    return driver;
  };

  const parseCar = (input) => {
    if (!input.trim()) return { car: null, carWear: null };
    const car = {}; const carWear = {};
    const lines = input.split('\n').map(l => l.trim()).filter(l => l);
    let currentKey = null;
    lines.forEach(line => {
      // Compact format (what /setup/race echoes back): "Chassis: 2 48%".
      const labelMatch = line.match(/^([A-Za-z\s]+):\s*(\d+)(?:\s+(\d+)%)?/);
      if (labelMatch) {
        const key = carKeyMap[labelMatch[1].trim().toLowerCase()];
        if (!key) { currentKey = null; return; }
        car[key] = parseInt(labelMatch[2], 10);
        currentKey = key;
        if (labelMatch[3]) carWear[key] = parseInt(labelMatch[3], 10);
        return;
      }
      // GPRO's own car page paste spreads level and wear over several lines
      // per part (label line, "Don't replace", then a "48%  2  48%" row) —
      // pick up the wear% from whichever later line has it, for the part
      // whose label line we saw most recently.
      if (currentKey && carWear[currentKey] === undefined) {
        const wearMatch = line.match(/^(\d+)%/);
        if (wearMatch) carWear[currentKey] = parseInt(wearMatch[1], 10);
      }
    });
    return { car, carWear };
  };

  const formatDriverInput = (driver) => {
    if (!driver) return '';
    return [
      `Energy: ${driver.energy ?? ''}`,
      `Overall: ${driver.overall ?? ''}`,
      `Concentration: ${driver.concentration ?? ''}`,
      `Talent: ${driver.talent ?? ''}`,
      `Aggressiveness: ${driver.aggressiveness ?? ''}`,
      `Experience: ${driver.experience ?? ''}`,
      `Technical Insight: ${driver.technicalInsight ?? ''}`,
      `Stamina: ${driver.stamina ?? ''}`,
      `Charisma: ${driver.charisma ?? ''}`,
      `Motivation: ${driver.motivation ?? ''}`,
      `Reputation: ${driver.reputation ?? ''}`,
      `Weight(kg): ${driver.weight ?? ''}`,
      `Age: ${driver.age ?? ''}`,
    ].join('\n');
  };

  const formatCarInput = (car, wearStart) => {
    if (!car || !wearStart) return '';
    const parts = [
      { label: 'Chassis', key: 'chassis' }, { label: 'Engine', key: 'engine' },
      { label: 'Front Wing', key: 'frontWing' }, { label: 'Rear Wing', key: 'rearWing' },
      { label: 'Underbody', key: 'underbody' }, { label: 'Sidepods', key: 'sidepods' },
      { label: 'Cooling', key: 'cooling' }, { label: 'Gearbox', key: 'gearbox' },
      { label: 'Brakes', key: 'brakes' }, { label: 'Suspension', key: 'suspension' },
      { label: 'Electronics', key: 'electronics' },
    ];
    return parts.map(p => `${p.label}: ${car[p.key] ?? ''} ${wearStart[p.key] ?? ''}%`).join('\n');
  };

  // Backend returns both formulas (current + martin) in one response — the toggle
  // just switches which one is displayed, no refetch needed.
  const runEnergyForecast = async (overrideDriver, overrideTrackId, overridePha) => {
    setEnergyForecast(null);
    setEnergyForecastError(null);
    setEnergyForecastLoading(true);
    try {
      const finalDriver = overrideDriver || parseDriver(driverInput);
      const finalTrackId = overrideTrackId || trackId;
      const energyPha = forceRefreshEnergy ? null : (overridePha !== undefined ? overridePha : cachedPha);
      const energyPayload = {
        ...(finalDriver && Object.keys(finalDriver).length > 0 && { driver: finalDriver }),
        isDry: weather.isDryRace,
        ...(finalTrackId && { trackId: finalTrackId }),
        ...(energyPha && energyPha),
      };
      const res = await api.post('/setup/race/energy-forecast', energyPayload);
      setEnergyForecast(res.data);
      if (res.data.carPower != null && res.data.carHandling != null && res.data.carAcceleration != null) {
        setCachedPha({ carPower: res.data.carPower, carHandling: res.data.carHandling, carAcceleration: res.data.carAcceleration });
      }
      if (forceRefreshEnergy) setForceRefreshEnergy(false);
    } catch (err) {
      setEnergyForecastError(err.response?.status === 403 ? 'Access denied.' : 'Failed to load forecast.');
    } finally {
      setEnergyForecastLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    const driver = parseDriver(driverInput);
    const { car, carWear } = parseCar(carInput);
    const stints = {};
    laps.forEach((v, i) => { if (v) stints[`stint${i + 1}`] = parseInt(v, 10); });
    const boostPayload = {};
    boosts.forEach((v, i) => { if (v && v.trim() !== '') boostPayload[`stint${i + 1}Boosts`] = parseInt(v, 10); });
    const testingStintsPayload = {};
    testingStints.forEach((v, i) => {
      const n = parseInt(v, 10);
      testingStintsPayload[i + 1] = isNaN(n) ? 0 : n;
    });
    const payload = {
      ...(driver && Object.keys(driver).length > 0 && { driver }),
      ...(car && Object.keys(car).length > 0 && { car }),
      ...(carWear && Object.keys(carWear).length > 0 && { carWear }),
      isDryQ1: weather.isDryQ1, isDryQ2: weather.isDryQ2,
      isDryRace: weather.isDryRace, isWetRace: weather.isWetRace,
      temperatureQ1: weather.temperatureQ1 === '' ? null : parseInt(weather.temperatureQ1, 10),
      temperatureQ2: weather.temperatureQ2 === '' ? null : parseInt(weather.temperatureQ2, 10),
      temperatureRace: weather.temperatureRace === '' ? null : parseInt(weather.temperatureRace, 10),
      ct, compound: selectedCompound, version,
      ...stints, ...boostPayload,
      testingStints: testingStintsPayload,
      ...(trackId && { trackId }),
      ...(impersonateUserId && { userId: parseInt(impersonateUserId, 10) }),
    };
    if (selectedSupplier && selectedSupplier.trim() !== '') payload.tyreSupplier = selectedSupplier;

    let carPha = null;
    let energyDriver = null;
    let energyTrackId = null;
    let finalSuspensionLevel = car?.suspension ?? null;
    try {
      const res = await api.post('/setup/race', payload);
      const data = res.data;
      if (data.today?.apiRequestsRemaining !== undefined) setApiRequests(data.today.apiRequestsRemaining);
      if (data.carPower != null && data.carHandling != null && data.carAcceleration != null) {
        carPha = { carPower: data.carPower, carHandling: data.carHandling, carAcceleration: data.carAcceleration };
        setCachedPha(carPha);
      }
      if (data.car?.suspension != null) finalSuspensionLevel = data.car.suspension;
      if (data.driver) energyDriver = data.driver;
      if (data.track?.id) energyTrackId = data.track.id;

      if (data.driver) setDriverInput(formatDriverInput(data.driver));
      if (data.car && data.carWearStart) setCarInput(formatCarInput(data.car, data.carWearStart));

      setQ1Data(data.q1 || {});
      setQ2Data(data.q2 || {});
      setRaceData(data.race || {});
      setTcdData(data.tcd || { q1: 0, q2: 0, race: 0 });
      setDriverStats(data.driver || {});
      setCarLevels(data.car || {});
      setCarWearStart(data.carWearStart || {});
      setCarWearFinish(data.carWearFinish || {});
      setCarWearFinishAndTestings(data.carWearFinishAndTestings || {});
      if (data.testingStints) {
        const sortedKeys = Object.keys(data.testingStints).map(k => parseInt(k, 10)).sort((a, b) => a - b);
        if (sortedKeys.length > 0) setTestingStints(sortedKeys.map(k => data.testingStints[k]));
      }
      setWearRecommendations(data.wearRecommendations || null);
      setFuelData(data.fuel || {});

      const keys = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8', 's9', 's10'];
      const newFuels = keys.map(k => data.fuel?.[k] !== undefined ? data.fuel[k].toString() : '');
      const newFuelWet = keys.map(k => data.fuelWet?.[k] !== undefined ? data.fuelWet[k].toString() : '');
      const newTyresLeft = keys.map(k => data.tyre?.[k] !== undefined ? data.tyre[k] + '%' : '');
      const newTyresLeftCorrected = keys.map(k => data.tyreCorrected?.[k] !== undefined ? data.tyreCorrected[k] + '%' : '');
      setFuels(newFuels);
      setFuelWet(newFuelWet);
      setTyresLeft(newTyresLeft);
      setTyresLeftCorrected(newTyresLeftCorrected);

      if (data.tyre) {
        setTyreTotals({
          total: data.tyre.total ?? '',
          totalExtraSoft: data.tyre.totalExtraSoft ?? '',
          totalSoft: data.tyre.totalSoft ?? '',
          totalMedium: data.tyre.totalMedium ?? '',
          totalHard: data.tyre.totalHard ?? '',
          totalRain: data.tyre.totalRain ?? '',
          model: data.tyre.model ?? '',
        });
      }
      if (data.tyreCorrected) {
        setTyreTotalsCorrected({
          total: data.tyreCorrected.total ?? '',
          totalExtraSoft: data.tyreCorrected.totalExtraSoft ?? '',
          totalSoft: data.tyreCorrected.totalSoft ?? '',
          totalMedium: data.tyreCorrected.totalMedium ?? '',
          totalHard: data.tyreCorrected.totalHard ?? '',
          totalRain: data.tyreCorrected.totalRain ?? '',
          model: data.tyreCorrected.model ?? 'corrected',
        });
      }
      if (data.tyre?.name) {
        const validCompounds = ['Extra Soft', 'Soft', 'Medium', 'Hard', 'Rain'];
        if (validCompounds.includes(data.tyre.name)) setSelectedCompound(data.tyre.name);
        if (tyreSuppliers.some(s => s.name === data.tyre.name)) setSelectedSupplier(data.tyre.name);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) setSubmitError('Access denied.');
      else if (err.response?.status === 404) setSubmitError("Selected user has no linked GPRO token — can't calculate for them.");
      else setSubmitError('Failed to calculate setup.');
    }

    // Prefer PHA fresh off this very submit; fall back to whatever we cached
    // from an earlier one (car/carWear can get re-sent on every submit once
    // the form auto-refills from the response, which stops /setup/race from
    // hitting Practice1 again — the cache is what keeps PHA available then).
    const effectivePha = carPha || cachedPha;

    if (canSeeSetupExtras) {
      await runEnergyForecast(energyDriver || driver, energyTrackId || trackId, forceRefreshEnergy ? null : effectivePha);
    }

    if (canSeeQ1Forecast) {
      setQ1Forecast(null);
      setQ1ForecastError(null);
      if (weather.temperatureQ1 === '') {
        setQ1ForecastError('Enter Q1 temperature to see the forecast.');
      } else {
        setQ1ForecastLoading(true);
        try {
          const finalDriver = energyDriver || driver;
          const finalTrackId = energyTrackId || trackId;
          const q1Pha = forceRefreshQ1 ? null : effectivePha;
          const basePayload = {
            ...(finalDriver && Object.keys(finalDriver).length > 0 && { driver: finalDriver }),
            temperatureQ1: parseInt(weather.temperatureQ1, 10),
            ...(finalTrackId && { trackId: finalTrackId }),
            ...(q1Pha && q1Pha),
            ...(finalSuspensionLevel != null && { suspensionLevel: finalSuspensionLevel }),
          };
          const res = await api.post('/setup/race/q1-forecast', basePayload);
          // Backend doesn't guarantee compound order (iterates its own map) — pin it.
          const compoundOrder = ['Extra Soft', 'Soft', 'Medium', 'Hard', 'Rain'];
          const forecasts = [...(res.data.forecasts || [])].sort(
            (a, b) => compoundOrder.indexOf(a.compound) - compoundOrder.indexOf(b.compound)
          );
          setQ1Forecast(forecasts);
          if (res.data.carPower != null && res.data.carHandling != null && res.data.carAcceleration != null) {
            setCachedPha({ carPower: res.data.carPower, carHandling: res.data.carHandling, carAcceleration: res.data.carAcceleration });
          }
          if (forceRefreshQ1) setForceRefreshQ1(false);
        } catch (err) {
          setQ1ForecastError(err.response?.status === 403 ? 'Access denied.' : 'Failed to load forecast.');
        } finally {
          setQ1ForecastLoading(false);
        }
      }
    }
  };

  const getSetupValue = (partKey, sessionData) => sessionData[partKey] !== undefined ? sessionData[partKey] : '';
  const raceTcdRace = tcdData.race * (totalLapsFromApi || 0);

  const carParts = [
    { label: 'Chassis', key: 'chassis' }, { label: 'Engine', key: 'engine' },
    { label: 'Front Wing', key: 'frontWing' }, { label: 'Rear Wing', key: 'rearWing' },
    { label: 'Underbody', key: 'underbody' }, { label: 'Sidepods', key: 'sidepods' },
    { label: 'Cooling', key: 'cooling' }, { label: 'Gearbox', key: 'gearbox' },
    { label: 'Brakes', key: 'brakes' }, { label: 'Suspension', key: 'suspension' },
    { label: 'Electronics', key: 'electronics' },
  ];

  const driverFields = [
    { label: 'Overall', key: 'overall' }, { label: 'Concentration', key: 'concentration' },
    { label: 'Talent', key: 'talent' }, { label: 'Aggressiveness', key: 'aggressiveness' },
    { label: 'Experience', key: 'experience' }, { label: 'Technical Insight', key: 'technicalInsight' },
    { label: 'Stamina', key: 'stamina' }, { label: 'Charisma', key: 'charisma' },
    { label: 'Motivation', key: 'motivation' }, { label: 'Reputation', key: 'reputation' },
    { label: 'Weight', key: 'weight' }, { label: 'Age', key: 'age' },
  ];

  // ── Render ───────────────────────────────────────────────────
  return (
    <>
      {/* Page header */}
      <div className="fb-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="fb-page-title">
            {title}
            {selectedTrackName && (
              <span style={{ fontWeight: 400, color: 'var(--c-text-muted)' }}> — {selectedTrackName}</span>
            )}
          </div>
          <div className="fb-page-sub">Race setup calculator - pure magic</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <TrackPicker currentTrackId={trackId} />
          <UserImpersonationPicker value={impersonateUserId} onChange={setImpersonateUserId} />

          {trackId && isPrivileged && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {trackGainsError && (
              <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{trackGainsError}</span>
            )}
            {trackGains && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
                background: '#fff', border: '1px solid var(--c-border)', borderRadius: 'var(--radius-sm)',
                padding: '6px 10px',
              }}>
                <span>
                  P: <strong style={{ color: phaColors.power }}>{trackGains.phaOffsetPower ?? '—'}</strong>
                  {'  '}H: <strong style={{ color: phaColors.handling }}>{trackGains.phaOffsetHandling ?? '—'}</strong>
                  {'  '}A: <strong style={{ color: phaColors.acceleration }}>{trackGains.phaOffsetAcceleration ?? '—'}</strong>
                </span>
                <span style={{ color: 'var(--c-border)' }}>|</span>
                <span>
                  Lap Gain @100CT: <strong>{trackGains.lapGain100Ct ?? '—'}</strong>
                  {'  '}Race Gain @100CT: <strong>{trackGains.raceGain100Ct ?? '—'}</strong>
                </span>
                {isAdmin && (
                  <button type="button" className="fb-btn fb-btn-ghost fb-btn-sm" onClick={() => setShowGainsModal(true)}>
                    Details
                  </button>
                )}
              </div>
            )}
            <button type="button" className="fb-btn fb-btn-secondary fb-btn-sm" onClick={() => setShowSimilarRacesModal(true)}>
              🔍 Similar races
            </button>
            <button type="button" className="fb-btn fb-btn-secondary fb-btn-sm"
              disabled={Object.keys(carLevels).length === 0}
              title={Object.keys(carLevels).length === 0 ? 'Submit the setup first' : undefined}
              onClick={() => setShowUpgradeModal(true)}>
              🔧 Recommend upgrade
            </button>
          </div>
          )}

          {trackId && trackId === todayTrackId && canSeeSimilarWeather
            && similarWeatherRaces && !similarWeatherRaces.definitelyDry
            && !!similarWeatherRaces.matches?.length && (
            <button type="button" className="fb-btn fb-btn-secondary fb-btn-sm"
              title="Historical races with the exact same weather forecast template as the upcoming race"
              onClick={() => setShowSimilarWeatherModal(true)}>
              🌧️ Similar weather ({similarWeatherRaces.matches.length})
            </button>
          )}
        </div>
      </div>

      {submitError && (
        <div className="fb-alert fb-alert-error" style={{ marginBottom: 16, marginTop: 0 }}>{submitError}</div>
      )}

      {showGainsModal && trackGains && (
        <TrackGainsModal
          trackId={trackId}
          gains={trackGains}
          onClose={() => setShowGainsModal(false)}
          onSaved={(fresh) => setTrackGains(fresh)}
        />
      )}
      {showSimilarRacesModal && (
        <SimilarRacesModal
          trackId={trackId}
          trackName={selectedTrackName}
          onClose={() => setShowSimilarRacesModal(false)}
        />
      )}
      {showSimilarWeatherModal && similarWeatherRaces && (
        <SimilarWeatherModal
          matches={similarWeatherRaces.matches}
          onClose={() => setShowSimilarWeatherModal(false)}
        />
      )}
      {showUpgradeModal && (
        <RecommendUpgradeModal
          trackId={trackId}
          trackName={selectedTrackName}
          car={carLevels}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
      {wearModalColumn && (
        <WearRecommendationsModal
          columnLabel={wearModalColumn === 'finish' ? 'Wear (finish)' : 'With Testing'}
          limited={wearModalColumn === 'finish' ? wearRecommendations?.limitedFinish : wearRecommendations?.limitedFinishAndTestings}
          extreme={wearModalColumn === 'finish' ? wearRecommendations?.extremeFinish : wearRecommendations?.extremeFinishAndTestings}
          onClose={() => setWearModalColumn(null)}
        />
      )}

      <form onSubmit={handleSubmit}>

        {/* ── Input data ── */}
        <div className="fb-card">
          <div className="fb-card-header"><span className="fb-card-title">Input Data</span></div>
          <div className="fb-card-body">
            <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label className="fb-label">Driver Stats</label>
                <textarea className="fb-textarea" name="driverInput" rows={14}
                  placeholder="Paste driver stats here…"
                  value={driverInput} onChange={(e) => setDriverInput(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label className="fb-label">Car Data</label>
                <textarea className="fb-textarea" name="carInput" rows={14}
                  placeholder="Paste car data here…"
                  value={carInput} onChange={(e) => setCarInput(e.target.value)} />
              </div>
            </div>
            <div className="fb-row">
              <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--c-border)', alignSelf: 'flex-end' }}>
                {[1, 2].map(v => (
                  <button key={v} type="button"
                    onClick={() => setVersion(v)}
                    style={{
                      padding: '6px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      background: version === v ? 'var(--c-accent)' : '#fff',
                      color: version === v ? '#fff' : 'var(--c-text)',
                      border: 'none', outline: 'none',
                    }}>
                    V{v}
                  </button>
                ))}
              </div>
              <div style={{ width: 100 }}>
                <label className="fb-label">CT</label>
                <input type="number" className="fb-input fb-input-sm" value={ct} placeholder="CT"
                  onChange={(e) => handleCtChange(parseInt(e.target.value, 10))} />
              </div>
              <div style={{ alignSelf: 'flex-end' }}>
                <button type="submit" className="fb-btn fb-btn-primary">Calculate Setup</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Weather & Wings row ── */}
        <div style={{ display: 'flex', gap: 16 }}>
          {/* Temperatures */}
          <div className="fb-card" style={{ flex: 2 }}>
            <div className="fb-card-header"><span className="fb-card-title">Weather</span></div>
            <div className="fb-table-wrap">
              <table className="fb-table">
                <thead>
                  <tr><th></th><th>Q1</th><th>Q2</th><th>Race</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 600, color: 'var(--c-text-muted)' }}>Temp</td>
                    {[
                      ['temperatureQ1', 'Q1'], ['temperatureQ2', 'Q2'], ['temperatureRace', 'Race'],
                    ].map(([field]) => (
                      <td key={field}>
                        <input type="number" className="fb-input fb-input-sm" style={{ width: 72 }}
                          value={weather[field]} onChange={(e) => handleTempChange(field, e.target.value)} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 600, color: 'var(--c-text-muted)' }}>Weather</td>
                    {[['Q1', 'isDryQ1', 'isWetQ1'], ['Q2', 'isDryQ2', 'isWetQ2'], ['Race', 'isDryRace', 'isWetRace']].map(([s, dry, wet]) => (
                      <td key={s}>
                        <label className="fb-checkbox" style={{ marginBottom: 4 }}>
                          <input type="checkbox" checked={weather[dry]} onChange={(e) => handleDryChange(s, e.target.checked)} /> Dry
                        </label>
                        <br />
                        <label className="fb-checkbox">
                          <input type="checkbox" checked={weather[wet]} onChange={(e) => handleWetChange(s, e.target.checked)} /> Wet
                        </label>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Wing split */}
          <div className="fb-card" style={{ flex: 1 }}>
            <div className="fb-card-header"><span className="fb-card-title">Wing Split</span></div>
            <div className="fb-card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {['Q1', 'Q2', 'Race'].map(s => (
                  <div key={s} className="fb-row">
                    <span style={{ width: 36, fontSize: 12, color: 'var(--c-text-muted)', fontWeight: 600 }}>{s}</span>
                    <input type="number" className="fb-input fb-input-sm" style={{ width: 72 }} />
                  </div>
                ))}
                <div className="fb-row" style={{ marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: 'var(--c-text-muted)', fontWeight: 600, width: 52 }}>Recalc</span>
                  <select className="fb-select" style={{ fontSize: 13, padding: '5px 28px 5px 10px' }}>
                    <option>Yes</option>
                    <option>No</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Setup & Driver & Car row ── */}
        <div style={{ display: 'flex', gap: 16 }}>
          {/* Setup table */}
          <div className="fb-card" style={{ flex: 2 }}>
            <div className="fb-card-header"><span className="fb-card-title">Setup</span></div>
            <div className="fb-table-wrap">
              <table className="fb-table">
                <thead>
                  <tr><th>Part</th><th>Q1</th><th>Q2</th><th>Race</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Front Wing', key: 'frontWing' },
                    { label: 'Rear Wing', key: 'rearWing' },
                    { label: 'Engine', key: 'engine' },
                    { label: 'Brakes', key: 'brakes' },
                    { label: 'Gearbox', key: 'gearbox' },
                    { label: 'Suspension', key: 'suspension' },
                    { label: 'TCD', key: 'tcd' },
                    { label: 'Race TCD', key: 'raceTcd' },
                  ].map((item) => (
                    <tr key={item.label}>
                      <td style={{ fontWeight: 600 }}>{item.label}</td>
                      {item.key === 'tcd' ? (
                        <><td>{tcdData.q1 ?? ''}</td><td>{tcdData.q2 ?? ''}</td><td>{tcdData.race ?? ''}</td></>
                      ) : item.key === 'raceTcd' ? (
                        <><td></td><td></td><td>{raceTcdRace.toFixed(2)}</td></>
                      ) : (
                        <>
                          <td>{getSetupValue(item.key, q1Data)}</td>
                          <td>{getSetupValue(item.key, q2Data)}</td>
                          <td>{getSetupValue(item.key, raceData)}</td>
                        </>
                      )}
                      <td style={{ color: 'var(--c-text-light)' }}>—</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Driver characteristics */}
          <div className="fb-card" style={{ flex: 1 }}>
            <div className="fb-card-header"><span className="fb-card-title">Driver</span></div>
            <div className="fb-table-wrap">
              <table className="fb-table fb-table-sm">
                <tbody>
                  {driverFields.map(({ label, key }) => (
                    <tr key={key}>
                      <td style={{ color: 'var(--c-text-muted)' }}>{label}</td>
                      <td style={{ fontWeight: 600 }}>{driverStats[key] ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Car Wear ── */}
        <div className="fb-card">
          <div className="fb-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <span className="fb-card-title">Car Wear</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {testingStints.map((v, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input type="number" className="fb-input fb-input-sm" style={{ width: 72 }}
                    min={0} max={50} value={v} title={`Stint ${i + 1}`}
                    onChange={(e) => handleTestingStintChange(i, e.target.value)} />
                  <button type="button" onClick={() => removeTestingStint(i)}
                    disabled={testingStints.length <= 1}
                    title="Remove stint"
                    style={{ border: 'none', background: 'none', color: 'var(--c-text-muted)', cursor: 'pointer', fontSize: 14, padding: '0 2px' }}>
                    ×
                  </button>
                </div>
              ))}
              <button type="button" className="fb-btn fb-btn-ghost fb-btn-sm" disabled={testingStints.length >= 10}
                title="Add stint" onClick={addTestingStint}>
                +
              </button>
              <button type="button" className="fb-btn fb-btn-ghost fb-btn-sm" title="Distribute 100 laps evenly"
                onClick={redistributeTestingStints}>
                ↻
              </button>
            </div>
          </div>
          <div className="fb-table-wrap">
            <table className="fb-table">
              <thead>
                <tr>
                  <th>Part</th><th>Level</th>
                  <th>Wear (start)</th>
                  <th>
                    Wear (finish)
                    {!!wearRecommendations?.limitedFinish?.length && (
                      <span title="Recommendations available — parts over 100% wear at finish" style={{ marginLeft: 4, cursor: 'pointer', color: 'var(--c-accent)' }}
                        onClick={() => setWearModalColumn('finish')}>★</span>
                    )}
                  </th>
                  <th>
                    With Testing
                    {!!wearRecommendations?.limitedFinishAndTestings?.length && (
                      <span title="Recommendations available — parts over 100% wear with testing" style={{ marginLeft: 4, cursor: 'pointer', color: 'var(--c-accent)' }}
                        onClick={() => setWearModalColumn('testing')}>★</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {carParts.map(({ label, key }) => (
                  <tr key={key}>
                    <td style={{ fontWeight: 600 }}>{label}</td>
                    <td>{carLevels[key] ?? '—'}</td>
                    <td>{carWearStart[key] !== undefined ? `${carWearStart[key]}%` : '—'}</td>
                    <td>{carWearFinish[key] !== undefined ? `${carWearFinish[key]}%` : '—'}</td>
                    <td>{carWearFinishAndTestings[key] !== undefined ? `${carWearFinishAndTestings[key]}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Tyres & Supplier ── */}
        <div style={{ display: 'flex', gap: 16 }}>
          {/* Tyre race wear */}
          <div className="fb-card" style={{ flex: 1 }}>
            <div className="fb-card-header"><span className="fb-card-title">Tyre Race Wear</span></div>
            <div className="fb-table-wrap">
              <table className="fb-table fb-table-sm">
                <thead>
                  <tr>
                    <th>Compound</th>
                    <th>Race Wear{tyreTotals.model ? ` (${tyreTotals.model})` : ''}</th>
                    <th>
                      Corrected estimate
                      <span
                        title="Alternative estimate, trained on this project's real race telemetry instead of the community formula. On historical validation it averages ~26 wear points of error vs ~33 (V1) / ~39 (V2) for the main formula — usually more accurate, but still a statistical model, not an exact game formula. Worth keeping an eye on both, especially if they diverge a lot."
                        style={{ marginLeft: 4, fontSize: 11, color: 'var(--c-text-muted)', cursor: 'help', fontWeight: 600 }}>
                        ⓘ
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Selected', key: 'total' },
                    { label: 'Extra Soft', key: 'totalExtraSoft' },
                    { label: 'Soft', key: 'totalSoft' },
                    { label: 'Medium', key: 'totalMedium' },
                    { label: 'Hard', key: 'totalHard' },
                    { label: 'Rain', key: 'totalRain' },
                  ].map(({ label, key }) => (
                    <tr key={key}>
                      <td>{label}</td>
                      <td style={{ fontWeight: 600 }}>{tyreTotals[key] || '—'}</td>
                      <td style={{ fontWeight: 600, color: 'var(--c-text-muted)' }}>{tyreTotalsCorrected[key] || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tyre settings */}
          <div className="fb-card" style={{ flex: 1 }}>
            <div className="fb-card-header"><span className="fb-card-title">Tyre Settings</span></div>
            <div className="fb-card-body">
              <div className="fb-form-group">
                <label className="fb-label">Supplier</label>
                <select className="fb-select" style={{ width: '100%' }}
                  value={selectedSupplier} onChange={(e) => setSelectedSupplier(e.target.value)}>
                  <option value="">Get from account</option>
                  {tyreSuppliers.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>
              <div className="fb-form-group">
                <label className="fb-label">Compound</label>
                <select className="fb-select" style={{ width: '100%' }}
                  value={selectedCompound} onChange={(e) => setSelectedCompound(e.target.value)}>
                  {['Extra Soft', 'Soft', 'Medium', 'Hard', 'Rain'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="fb-form-group">
                <label className="fb-label">CT</label>
                <input type="number" className="fb-input" value={ct} placeholder="CT"
                  onChange={(e) => handleCtChange(parseInt(e.target.value, 10))} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Forecasts ── */}
        <div style={{ display: 'flex', gap: 16 }}>
          {/* Energy forecast (ADMIN/UPDATER/FORCE_ONE) */}
          {canSeeSetupExtras && (
            <div className="fb-card" style={{ flex: 1 }}>
              <div className="fb-card-header">
                <span className="fb-card-title">Energy Forecast</span>
                <label
                  title="Ignore the cached car power/handling/acceleration and have the backend re-fetch it live from GPRO on next submit — eats 1 GPRO token. Use if you changed the car and want an up-to-date forecast."
                  style={{ fontSize: 11, color: 'var(--c-text-muted)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type="checkbox" checked={forceRefreshEnergy}
                    onChange={(e) => setForceRefreshEnergy(e.target.checked)} />
                  refresh
                </label>
              </div>
              <div className="fb-card-body">
                <div style={{ display: 'flex', gap: 2, marginBottom: 10 }}>
                  {[['CURRENT', 'Fobase'], ['MARTIN', "Martin's formula"]].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      title={value === 'MARTIN' ? "Alternative model (Martin Rosina's spreadsheet formula) — for comparison, not the production model." : undefined}
                      onClick={() => setEnergyFormula(value)}
                      style={{
                        flex: 1, fontSize: 12, fontWeight: 600, padding: '4px 8px', cursor: 'pointer',
                        border: '1px solid var(--c-border)',
                        background: energyFormula === value ? 'var(--c-accent-glow)' : 'transparent',
                        color: energyFormula === value ? 'var(--c-accent)' : 'var(--c-text-muted)',
                      }}>
                      {label}
                    </button>
                  ))}
                </div>
                {energyForecastLoading && (
                  <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>Calculating…</div>
                )}
                {!energyForecastLoading && energyForecastError && (
                  <div style={{ color: 'var(--c-accent)', fontSize: 13 }}>{energyForecastError}</div>
                )}
                {!energyForecastLoading && !energyForecastError && !energyForecast && (
                  <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>Submit setup to see the forecast.</div>
                )}
                {!energyForecastLoading && !energyForecastError && energyForecast && (() => {
                  const selected = energyFormula === 'MARTIN' ? energyForecast.martin : energyForecast.current;
                  if (!selected) return null;
                  const ctSteps = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
                  const nearestOf = (target) => target == null ? null
                    : ctSteps.reduce((best, v) => Math.abs(v - target) < Math.abs(best - target) ? v : best, ctSteps[0]);
                  const maxCtSafe = selected.maxCtSafe ?? selected.maxCtForFullEnergy;
                  const maxCtExpected = selected.maxCtExpected;
                  const safeStep = nearestOf(maxCtSafe);
                  const expectedStep = maxCtExpected != null ? nearestOf(maxCtExpected) : null;
                  return (
                    <>
                      <div className="fb-table-wrap">
                        <table className="fb-table fb-table-sm">
                          <thead><tr><th>CT</th><th>Energy</th></tr></thead>
                          <tbody>
                            {ctSteps.map(ctStep => {
                              const val = selected.energyByCt?.[ctStep];
                              const unc = selected.uncertaintyByCt?.[ctStep];
                              const rare = !!selected.rareByCt?.[ctStep];
                              const overBudget = val !== undefined && val > 100;
                              const isSafe = ctStep === safeStep;
                              const isExpected = ctStep === expectedStep && !isSafe;
                              const rowStyle = {
                                ...(isSafe ? { background: 'var(--c-accent-glow)' } : {}),
                                ...(isExpected ? { boxShadow: 'inset 0 0 0 1px var(--c-border)' } : {}),
                                ...(rare ? { borderLeft: '2px dashed var(--c-text-light)' } : {}),
                              };
                              return (
                                <tr key={ctStep} style={rowStyle}>
                                  <td style={{ fontWeight: isSafe ? 700 : 400 }}>
                                    {ctStep}
                                    {isSafe && <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--c-accent)' }}>safe</span>}
                                    {isExpected && <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--c-text-muted)' }}>expected</span>}
                                    {rare && <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--c-text-light)', cursor: 'help' }} title="Few similar historical races — lower confidence">◌</span>}
                                  </td>
                                  <td style={{ fontWeight: 600, color: overBudget ? 'var(--c-error)' : undefined }}>
                                    {val !== undefined ? val.toFixed(1) : '—'}
                                    {unc != null && (
                                      <span style={{ fontSize: 10, color: 'var(--c-text-muted)', fontWeight: 400, marginLeft: 3 }}>
                                        ±{unc.toFixed(1)}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: 10, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div>Recommended: CT ≤ <strong>{maxCtSafe ?? '—'}</strong></div>
                        {maxCtExpected != null && (
                          <div style={{ color: 'var(--c-text-muted)' }}>
                            Limit (50/50): CT ≤ <strong style={{ color: 'var(--c-text)' }}>{maxCtExpected}</strong>
                            {selected.safetyMargin != null && ` (${selected.safetyMargin} margin)`}
                          </div>
                        )}
                        {selected.trackSampleCount != null && (
                          <div style={{ color: 'var(--c-text-light)', fontSize: 11 }}>
                            Based on {selected.trackSampleCount} similar races
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Q1 forecast (ADMIN/FORCE_ONE) */}
          {canSeeQ1Forecast && (
            <div className="fb-card" style={{ flex: 1 }}>
              <div className="fb-card-header">
                <span className="fb-card-title">Q1 Forecast</span>
                <label
                  title="Ignore the cached car power/handling/acceleration and have the backend re-fetch it live from GPRO on next submit — eats 1 GPRO token. Use if you changed the car and want an up-to-date forecast."
                  style={{ fontSize: 11, color: 'var(--c-text-muted)', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type="checkbox" checked={forceRefreshQ1}
                    onChange={(e) => setForceRefreshQ1(e.target.checked)} />
                  refresh
                </label>
              </div>
              <div className="fb-card-body">
                {q1ForecastLoading && (
                  <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>Calculating…</div>
                )}
                {!q1ForecastLoading && q1ForecastError && (
                  <div style={{ color: 'var(--c-accent)', fontSize: 13 }}>{q1ForecastError}</div>
                )}
                {!q1ForecastLoading && !q1ForecastError && !q1Forecast && (
                  <div style={{ color: 'var(--c-text-muted)', fontSize: 13 }}>Submit setup to see the forecast.</div>
                )}
                {!q1ForecastLoading && !q1ForecastError && q1Forecast && (
                  <table className="fb-table fb-table-sm">
                    <thead><tr><th>Compound</th><th>Q1 time</th></tr></thead>
                    <tbody>
                      {q1Forecast.map(f => (
                        <tr key={f.compound}>
                          <td>{f.compound}</td>
                          <td style={{ fontWeight: 600 }}>{f.predictedFormatted ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Stints ── */}
        <div className="fb-card">
          <div className="fb-card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
            <span className="fb-card-title">Stints</span>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {compoundTactics.map(({ key, label }) => {
                  const totalWear = tyreTotals[key];
                  const hasData = typeof totalWear === 'number' && totalLapsFromApi;
                  if (!hasData) {
                    return (
                      <button key={key} type="button" disabled
                        className="fb-btn fb-btn-secondary fb-btn-sm" title="No data yet">
                        {label} · —
                      </button>
                    );
                  }
                  const raceLaps = Number(totalLapsFromApi);
                  const tactics = getCompoundTactics(totalWear, raceLaps);
                  return tactics.map((t, idx) => {
                    const pits = t.stints - 1;
                    return (
                      <button key={`${key}-${idx}`} type="button"
                        className={`fb-btn fb-btn-sm ${t.fits ? 'fb-btn-secondary' : 'fb-btn-ghost'}`}
                        onClick={() => applyTactic(label, t.laps)}
                        title={t.fits
                          ? `${label}: ${t.stints} stint${t.stints !== 1 ? 's' : ''} (${pits} pit${pits !== 1 ? 's' : ''}) — laps: ${t.laps.join('+')}`
                          : `${label}: even split, does not respect the wobbling tolerance — laps: ${t.laps.join('+')}`}>
                        {label} · {pits} pit{pits !== 1 ? 's' : ''}{!t.fits ? ' ⚠' : ''}
                      </button>
                    );
                  });
                })}
              </div>

              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--c-text-muted)', fontWeight: 600 }}>Wobbling</span>
                <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--c-border)' }}>
                  {['0', '1', '2', '3', 'unlimited'].map(v => (
                    <button key={v} type="button"
                      onClick={() => setWobbling(v)}
                      style={{
                        padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        background: wobbling === v ? 'var(--c-accent)' : '#fff',
                        color: wobbling === v ? '#fff' : 'var(--c-text)',
                        border: 'none', outline: 'none',
                      }}>
                      {v === 'unlimited' ? '∞' : v}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--c-text-muted)', fontWeight: 600 }}>Overcut</span>
                <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--c-border)' }}>
                  {['Yes', 'No'].map(v => (
                    <button key={v} type="button"
                      onClick={() => setOvercut(v)}
                      style={{
                        padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        background: overcut === v ? 'var(--c-accent)' : '#fff',
                        color: overcut === v ? '#fff' : 'var(--c-text)',
                        border: 'none', outline: 'none',
                      }}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="fb-table-wrap">
            <table className="fb-table fb-table-sm">
              <thead>
                <tr>
                  <th style={{ minWidth: 110 }}></th>
                  {laps.map((_, i) => <th key={i}>Stint {i + 1}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 600 }}>
                    Laps&nbsp;
                    <span style={{ color: 'var(--c-text-muted)', fontWeight: 400 }}>
                      ({totalEnteredLaps}/{totalLapsFromApi || '?'})
                    </span>
                  </td>
                  {laps.map((v, i) => (
                    <td key={i}>
                      <input type="number" min="1" max="1000" value={v}
                        onChange={(e) => handleLapChange(i, e.target.value)} />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td style={{ fontWeight: 600 }}>Boost</td>
                  {boosts.map((v, i) => (
                    <td key={i}>
                      <input type="number" min="1" max="9" value={v}
                        onChange={(e) => handleBoostChange(i, e.target.value)} />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td style={{ color: 'var(--c-text-muted)' }}>Fuel</td>
                  {fuels.map((v, i) => <td key={i} style={{ fontWeight: 600 }}>{v}</td>)}
                </tr>
                <tr>
                  <td style={{ color: 'var(--c-text-muted)' }}>Fuel (wet)</td>
                  {fuelWet.map((v, i) => <td key={i} style={{ fontWeight: 600 }}>{v}</td>)}
                </tr>
                <tr>
                  <td style={{ color: 'var(--c-text-muted)' }}>Tyres left</td>
                  {tyresLeft.map((v, i) => (
                    <td key={i} style={{ fontWeight: 600 }}>
                      {v}
                      {!!tyresLeftCorrected[i] && (
                        <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--c-text-muted)' }} title="Corrected estimate">
                          {tyresLeftCorrected[i]}
                        </div>
                      )}
                      {!!stintWobblingLaps[i] && (
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--c-accent)' }}>
                          ⚠ {stintWobblingLaps[i]} wobbling
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ textAlign: 'right', paddingBottom: 12 }}>
          <button type="submit" className="fb-btn fb-btn-primary">Submit Setup</button>
        </div>
      </form>
    </>
  );
};

export default SetupPage;
